import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import {
  PunchoutAuditDateRange,
  PunchoutAuditEventType,
  PunchoutAuditLogEntry,
  PunchoutOrderIntakeStatus
} from '../../models/punchout';

// This bypasses the gung request-cache interceptor. Admin reads must always show the latest
// write. See request-cache.service.ts:42 for the header contract.
const NO_CACHE_HEADERS = { maxAge: '-1' };

/**
 * Admin reads of the punchout audit log. All endpoints here require ADMIN, which the settings
 * route already enforces via AdminGuard.
 */
@Injectable({ providedIn: 'root' })
export class PunchoutAuditAdminService {
  protected http = inject(HttpClient);
  private readonly base = 'json/punchout/audit';

  /**
   * Entries with the newest entry first. A request for PUNCHOUT_ORDER_MESSAGE also gives the entries
   * that the backend stored before the type field was available.
   *
   * The response has no paging and includes the raw document and the payload maps. A caller that
   * makes a list view must remove these fields. Always give a date range. Then the response has a
   * known maximum size.
   */
  list(
    type?: PunchoutAuditEventType,
    status?: PunchoutOrderIntakeStatus,
    configId?: string,
    range?: PunchoutAuditDateRange
  ): Observable<PunchoutAuditLogEntry[]> {
    let params = new HttpParams();
    if (type) {
      params = params.set('type', type);
    }
    if (status) {
      params = params.set('status', status);
    }
    if (configId) {
      params = params.set('configId', configId);
    }
    if (range?.from) {
      params = params.set('from', range.from);
    }
    if (range?.to) {
      params = params.set('to', range.to);
    }
    return this.http.get<PunchoutAuditLogEntry[]>(this.base, { params, headers: NO_CACHE_HEADERS });
  }

  get(id: string): Observable<PunchoutAuditLogEntry> {
    return this.http.get<PunchoutAuditLogEntry>(`${this.base}/${encodeURIComponent(id)}`, {
      headers: NO_CACHE_HEADERS
    });
  }

  /**
   * Re-runs order intake for one entry, regardless of its current status. This bypasses payload
   * dedup on the backend. It can create a second order from a document that was already
   * processed. Always confirm with the user before calling this.
   *
   * This call fails with a 500 when the app ships punchout without the EDI module. Gate the
   * call on the gung-feature-edi backend feature.
   */
  process(id: string): Observable<PunchoutAuditLogEntry> {
    return this.http.post<PunchoutAuditLogEntry>(`${this.base}/${encodeURIComponent(id)}/process`, {});
  }
}
